// Code Attrib: https://usehooks.com/useAsync/

import { useIsMounted } from './use-is-mounted';
import { useState, useCallback, useEffect } from 'react';

export type AsyncCallStatus = 'idle' | 'pending' | 'completed';

export const useAsync = <T, E = string>(
  asyncFunction: () => Promise<T>,
  immediate = true,
) => {
  const isMounted = useIsMounted();
  const [status, setStatus] = useState<AsyncCallStatus>('idle');
  const [value, setValue] = useState<T | null>(null);
  const [error, setError] = useState<E | null>(null);
  // The execute function wraps asyncFunction and
  // handles setting state for pending, value, and error.
  // useCallback ensures the below useEffect is not called
  // on every render, but only if asyncFunction changes.
  const execute = useCallback(() => {
    setStatus('pending');
    setValue(null);
    setError(null);
    asyncFunction()
      .then((response) => {
        if (isMounted.current) {
          setValue(response);
          setStatus('completed');
        }
      })
      .catch((error) => {
        if (isMounted.current) {
          setError(error);
          setStatus('completed');
        }
      });
  }, [asyncFunction, isMounted]);
  // Call execute if we want to fire it right away.
  // Otherwise execute can be called later, such as
  // in an onClick handler.
  useEffect(() => {
    if (immediate) {
      execute();
    }
  }, [execute, immediate]);
  return { execute, status, value, error };
};
